import React, { useState, useRef, useEffect } from 'react';

interface RecipeImageWithFallbackProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  lazy?: boolean;
}

export function RecipeImageWithFallback(props: RecipeImageWithFallbackProps) {
  const [didError, setDidError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!props.lazy);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!props.lazy || isInView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [props.lazy, isInView]);

  const handleError = () => {
    setDidError(true);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const { src, alt, style, className, lazy, ...rest } = props;

  // Show placeholder while not in view or loading
  if (!isInView) {
    return (
      <div
        ref={imgRef}
        className={`bg-gray-200 animate-pulse ${className ?? ''}`}
        style={style}
        {...rest}
      />
    );
  }

  return didError || !src ? (
    <img
      ref={imgRef}
      src="/recipe_fb.png"
      alt={alt || 'Recipe image'}
      className={`${className ?? ''} ${
        !isLoaded ? 'opacity-0' : 'opacity-100'
      } transition-opacity duration-300`}
      style={style}
      {...rest}
      onLoad={handleLoad}
    />
  ) : (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={`${className ?? ''} ${
        !isLoaded ? 'opacity-0' : 'opacity-100'
      } transition-opacity duration-300`}
      style={style}
      {...rest}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
}
